import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  Grid,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Typography,
} from "@mui/material";
import { useFormikContext } from "formik";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import useJumboAuth from "@jumbo/hooks/useJumboAuth";
import { getCustomers } from "app/services/api/management";
import { useParams } from "react-router-dom";

const ALLOWED_CUSTOMER_TYPES = ["standalone", "parent", "child"];
const ALLOWED_RELATIONSHIP_TYPES = ["multiLicense", "multiKiosk"];

const RelationsTab = ({ readOnly = false }) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { getAuthUser } = useJumboAuth();
  const { customerId: authCustomerId } = getAuthUser() || {};
  const { url: routeCustomerUrl, customerUrl: routeCustomerUrlFromSettings } =
    useParams();

  const { values, errors, touched, setFieldValue, setFieldTouched } =
    useFormikContext();

  const currentCustomerUrl = useMemo(() => {
    const v =
      routeCustomerUrl ??
      routeCustomerUrlFromSettings ??
      values?.customer?.url ??
      "";
    return v ? String(v) : "";
  }, [routeCustomerUrl, routeCustomerUrlFromSettings, values?.customer?.url]);

  const [parentCustomers, setParentCustomers] = useState([]);
  const parentRelationshipByIdRef = useRef({});
  const [isLoadingParents, setIsLoadingParents] = useState(false);
  const parentsLoadedRef = useRef(false);

  const customerType = values?.customer?.customerRelation?.customerType;
  const parentCustomerId = values?.customer?.customerRelation?.parentCustomerId;
  const relationshipType = values?.customer?.customerRelation?.relationshipType;

  // Ensure sane defaults in the model.
  useEffect(() => {
    if (readOnly) return;
    if (!ALLOWED_CUSTOMER_TYPES.includes(customerType)) {
      setFieldValue("customer.customerRelation.customerType", "standalone");
    }
  }, [customerType, setFieldValue, readOnly]);

  // Enforce conditional model rules based on customerType.
  useEffect(() => {
    if (readOnly) return;
    if (customerType !== "child" && parentCustomerId !== null) {
      setFieldValue("customer.customerRelation.parentCustomerId", null);
    }
    if (customerType === "standalone" && parentCustomerId !== null) {
      setFieldValue("customer.customerRelation.parentCustomerId", null);
    }
    if (customerType === "standalone" && relationshipType !== null) {
      setFieldValue("customer.customerRelation.relationshipType", null);
    }
  }, [customerType, parentCustomerId, relationshipType, setFieldValue, readOnly]);

  const normalizedCustomerType = ALLOWED_CUSTOMER_TYPES.includes(customerType)
    ? customerType
    : "standalone";

  const showParentCustomerId = normalizedCustomerType === "child";
  const showRelationshipType = normalizedCustomerType === "parent";
  const showInheritedRelationshipType = normalizedCustomerType === "child";

  const parentCustomerError =
    errors?.customer?.customerRelation?.parentCustomerId;
  const parentCustomerTouched =
    touched?.customer?.customerRelation?.parentCustomerId;
  const showParentCustomerError = Boolean(
    showParentCustomerId && parentCustomerTouched && parentCustomerError,
  );

  const selectedParentLabel = useMemo(() => {
    if (!parentCustomerId) return "";
    const match = parentCustomers.find((c) => c.value === parentCustomerId);
    return match?.label || String(parentCustomerId);
  }, [parentCustomerId, parentCustomers]);

  useEffect(() => {
    const parseDsokSettings = (dsokSettings) => {
      if (!dsokSettings) return null;
      if (typeof dsokSettings === "string") {
        try {
          return JSON.parse(dsokSettings);
        } catch (e) {
          return null;
        }
      }
      return dsokSettings;
    };

    const loadParents = async () => {
      if (!showParentCustomerId) return;
      if (parentsLoadedRef.current) return;
      if (!authCustomerId) return;

      setIsLoadingParents(true);
      try {
        const result = await getCustomers(authCustomerId);
        if (!Array.isArray(result)) {
          throw new Error("getCustomers returned non-array");
        }

        const customersData = result.map((c) => ({
          ...c.customerData,
          users: c.users,
        }));

        const parentOnly = customersData.filter((c) => {
          const dsokSettings = parseDsokSettings(c.dsokSettings);
          const relationType =
            dsokSettings?.customer?.customerRelation?.customerType ??
            c?.customerRelation?.customerType;
          return relationType === "parent";
        });

        // Build a lookup for relationshipType inheritance.
        const relationshipById = {};
        parentOnly.forEach((c) => {
          const dsokSettings = parseDsokSettings(c.dsokSettings);
          const rel =
            dsokSettings?.customer?.customerRelation ??
            c?.customerRelation ??
            null;
          const value = String(c?.url ?? c?.customerId ?? c?.id ?? "");
          if (!value) return;
          const rt = rel?.relationshipType;
          if (ALLOWED_RELATIONSHIP_TYPES.includes(rt)) {
            relationshipById[value] = rt;
          }
        });
        parentRelationshipByIdRef.current = relationshipById;

        const options = parentOnly
          .map((c) => {
            const value = String(c?.url ?? c?.customerId ?? c?.id ?? "");
            const name =
              c?.companyName ?? c?.customerName ?? c?.name ?? c?.title ?? "";
            const storeCode = c?.storeCode ?? "";
            const cid = c?.customerId ?? c?.id ?? "";
            const labelBase =
              `${storeCode ? `${storeCode} - ` : ""}${name}`.trim();
            const label = `${labelBase || value} (ID: ${cid || value})`;

            return value
              ? {
                  value,
                  label,
                }
              : null;
          })
          .filter(Boolean)
          .filter((o) => (currentCustomerUrl ? o.value !== currentCustomerUrl : true))
          .sort((a, b) => a.label.localeCompare(b.label));

        setParentCustomers(options);
        parentsLoadedRef.current = true;
      } catch (e) {
        enqueueSnackbar(
          t(
            "pages.dsokDetails.customerSettingsTabs.relations.parentCustomerId.error",
          ),
          { variant: "error" },
        );
        setParentCustomers([]);
        parentsLoadedRef.current = true; // don't spam backend on rerenders
      } finally {
        setIsLoadingParents(false);
      }
    };

    loadParents();
  }, [
    authCustomerId,
    currentCustomerUrl,
    enqueueSnackbar,
    showParentCustomerId,
    t,
  ]);

  // Inherit relationshipType from the selected parent when current customer is a child.
  useEffect(() => {
    if (normalizedCustomerType !== "child") return;
    if (!parentCustomerId) {
      if (relationshipType !== null) {
        setFieldValue("customer.customerRelation.relationshipType", null);
      }
      return;
    }

    const inherited =
      parentRelationshipByIdRef.current?.[String(parentCustomerId)];
    if (!inherited) {
      // Parent exists but has no relationshipType set yet.
      setFieldValue("customer.customerRelation.relationshipType", null);
      return;
    }

    if (relationshipType !== inherited) {
      setFieldValue("customer.customerRelation.relationshipType", inherited);
    }
  }, [
    normalizedCustomerType,
    parentCustomerId,
    relationshipType,
    setFieldValue,
  ]);

  const relationshipValue = ALLOWED_RELATIONSHIP_TYPES.includes(
    relationshipType,
  )
    ? relationshipType
    : "";

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} md={12}>
        <FormControl component="fieldset">
          <FormLabel component="legend">
            {t(
              "pages.dsokDetails.customerSettingsTabs.relations.customerType.label",
            )}
          </FormLabel>
          <FormHelperText sx={{ mb: 1 }}>
            {t(
              "pages.dsokDetails.customerSettingsTabs.relations.customerType.help",
            )}
          </FormHelperText>
          <RadioGroup
            value={normalizedCustomerType}
            onChange={(e) =>
              !readOnly &&
              setFieldValue(
                "customer.customerRelation.customerType",
                e.target.value,
              )
            }
          >
            <FormControlLabel
              value="standalone"
              control={<Radio disabled={readOnly} />}
              label={t(
                "pages.dsokDetails.customerSettingsTabs.relations.customerType.options.standalone",
              )}
            />
            <FormControlLabel
              value="parent"
              control={<Radio disabled={readOnly} />}
              label={t(
                "pages.dsokDetails.customerSettingsTabs.relations.customerType.options.parent",
              )}
            />
            <FormControlLabel
              value="child"
              control={<Radio disabled={readOnly} />}
              label={t(
                "pages.dsokDetails.customerSettingsTabs.relations.customerType.options.child",
              )}
            />
          </RadioGroup>
        </FormControl>
      </Grid>

      {showParentCustomerId && (
        <Grid item xs={12} md={6} lg={5}>
          <FormControl
            fullWidth
            required
            error={showParentCustomerError}
            disabled={readOnly || isLoadingParents || parentCustomers.length === 0}
          >
            <FormLabel>
              {t(
                "pages.dsokDetails.customerSettingsTabs.relations.parentCustomerId.label",
              )}
            </FormLabel>
            <Select
              value={parentCustomerId ?? ""}
              displayEmpty
              onBlur={() =>
                setFieldTouched(
                  "customer.customerRelation.parentCustomerId",
                  true,
                  true,
                )
              }
              onChange={(e) =>
                !readOnly &&
                setFieldValue(
                  "customer.customerRelation.parentCustomerId",
                  e.target.value || null,
                )
              }
              renderValue={(selected) => {
                if (!selected) {
                  return t(
                    "pages.dsokDetails.customerSettingsTabs.relations.parentCustomerId.placeholder",
                  );
                }
                return selectedParentLabel;
              }}
            >
              <MenuItem value="" disabled>
                {isLoadingParents ? (
                  <Grid container alignItems="center" gap={1}>
                    <CircularProgress size={16} />
                    <Typography variant="body2">
                      {t(
                        "pages.dsokDetails.customerSettingsTabs.relations.parentCustomerId.loading",
                      )}
                    </Typography>
                  </Grid>
                ) : (
                  t(
                    "pages.dsokDetails.customerSettingsTabs.relations.parentCustomerId.placeholder",
                  )
                )}
              </MenuItem>
              {parentCustomers.map((c) => (
                <MenuItem key={c.value} value={c.value}>
                  {c.label}
                </MenuItem>
              ))}
            </Select>
            <FormHelperText>
              {showParentCustomerError
                ? String(parentCustomerError)
                : isLoadingParents
                  ? t(
                      "pages.dsokDetails.customerSettingsTabs.relations.parentCustomerId.loading",
                    )
                  : parentCustomers.length === 0
                    ? t(
                        "pages.dsokDetails.customerSettingsTabs.relations.parentCustomerId.empty",
                      )
                    : t(
                        "pages.dsokDetails.customerSettingsTabs.relations.parentCustomerId.help",
                      )}
            </FormHelperText>
          </FormControl>
        </Grid>
      )}

      {showRelationshipType && (
        <Grid item xs={12} md={12}>
          <FormControl component="fieldset">
            <FormLabel component="legend">
              {t(
                "pages.dsokDetails.customerSettingsTabs.relations.relationshipType.label",
              )}
            </FormLabel>
            <FormHelperText sx={{ mb: 1 }}>
              {t(
                "pages.dsokDetails.customerSettingsTabs.relations.relationshipType.help",
              )}
            </FormHelperText>
            <RadioGroup
              value={relationshipValue}
              onChange={(e) =>
                !readOnly &&
                setFieldValue(
                  "customer.customerRelation.relationshipType",
                  e.target.value,
                )
              }
            >
              <FormControlLabel
                value="multiLicense"
                control={<Radio disabled={readOnly} />}
                label={t(
                  "pages.dsokDetails.customerSettingsTabs.relations.relationshipType.options.multiLicense",
                )}
              />
              <FormControlLabel
                value="multiKiosk"
                control={<Radio disabled={readOnly} />}
                label={t(
                  "pages.dsokDetails.customerSettingsTabs.relations.relationshipType.options.multiKiosk",
                )}
              />
            </RadioGroup>
          </FormControl>
        </Grid>
      )}

      {showInheritedRelationshipType && (
        <Grid item xs={12} md={6} lg={5}>
          <FormControl fullWidth disabled={true}>
            <FormLabel>
              {t(
                "pages.dsokDetails.customerSettingsTabs.relations.relationshipType.label",
              )}
            </FormLabel>
            <Select value={relationshipType ?? ""} displayEmpty>
              <MenuItem value="">
                {t(
                  "pages.dsokDetails.customerSettingsTabs.relations.relationshipType.inheritedEmpty",
                  {
                    defaultValue: "Inherited from parent",
                  },
                )}
              </MenuItem>
              <MenuItem value="multiLicense">
                {t(
                  "pages.dsokDetails.customerSettingsTabs.relations.relationshipType.options.multiLicense",
                )}
              </MenuItem>
              <MenuItem value="multiKiosk">
                {t(
                  "pages.dsokDetails.customerSettingsTabs.relations.relationshipType.options.multiKiosk",
                )}
              </MenuItem>
            </Select>
            <FormHelperText>
              {t(
                "pages.dsokDetails.customerSettingsTabs.relations.relationshipType.inheritedHelp",
                {
                  defaultValue: "Relationship type is inherited from the parent customer.",
                },
              )}
            </FormHelperText>
          </FormControl>
        </Grid>
      )}
    </Grid>
  );
};

export default RelationsTab;
